import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './AppIcon';

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard', section: 'Navigation' },
  { label: 'Leads', path: '/leads', icon: 'UserPlus', section: 'Navigation' },
  { label: 'Jobs', path: '/jobs', icon: 'Briefcase', section: 'Navigation' },
  { label: 'Contacts', path: '/contacts', icon: 'Users', section: 'Navigation' },
  { label: 'Accounts', path: '/accounts', icon: 'Building2', section: 'Navigation' },
  { label: 'Deals', path: '/deals', icon: 'Target', section: 'Navigation' },
  { label: 'Pipeline', path: '/pipeline', icon: 'GitBranch', section: 'Navigation' },
  { label: 'Calendar', path: '/calendar', icon: 'CalendarDays', section: 'Navigation' },
  { label: 'Activities', path: '/activities', icon: 'Activity', section: 'Navigation' },
  { label: 'Tasks', path: '/tasks', icon: 'CheckSquare', section: 'Navigation' },
  { label: 'Emails', path: '/emails', icon: 'Mail', section: 'Navigation' },
  { label: 'Reports', path: '/reports', icon: 'BarChart3', section: 'Navigation' },
  { label: 'AI Voice', path: '/ai-voice', icon: 'Phone', section: 'Navigation' },
  { label: 'AI Marketing', path: '/ai-marketing', icon: 'Zap', section: 'Navigation' },
  { label: 'Billing', path: '/billing', icon: 'CreditCard', section: 'Navigation' },
  { label: 'Settings', path: '/settings', icon: 'Settings', section: 'Navigation' },
  { label: 'Integrations', path: '/integrations', icon: 'Puzzle', section: 'Navigation' },
];

const QUICK_ACTIONS = [
  { label: 'New Lead', icon: 'UserPlus', action: 'new_lead', section: 'Quick Actions', path: '/leads' },
  { label: 'New Job', icon: 'Briefcase', action: 'new_job', section: 'Quick Actions', path: '/jobs' },
  { label: 'New Contact', icon: 'Users', action: 'new_contact', section: 'Quick Actions', path: '/contacts' },
  { label: 'New Deal', icon: 'Target', action: 'new_deal', section: 'Quick Actions', path: '/deals' },
];

const RECENT_KEY = 'wilcart_recent_pages';

const getRecent = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch {
    return [];
  }
};

const addRecent = (item) => {
  try {
    const existing = getRecent()?.filter(r => r?.path !== item?.path);
    const updated = [item, ...existing]?.slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
  } catch {}
};

const CommandPalette = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recent, setRecent] = useState([]);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setRecent(getRecent());
      setTimeout(() => inputRef?.current?.focus(), 50);
    }
  }, [isOpen]);

  // Track page visits
  useEffect(() => {
    const match = NAV_ITEMS?.find(n => n?.path === location?.pathname);
    if (match) addRecent({ label: match?.label, path: match?.path, icon: match?.icon });
  }, [location?.pathname]);

  const recentItems = recent?.map(r => ({ ...r, section: 'Recent' }));

  const allItems = query?.trim()
    ? [
        ...NAV_ITEMS?.filter(i => i?.label?.toLowerCase()?.includes(query?.toLowerCase())),
        ...QUICK_ACTIONS?.filter(i => i?.label?.toLowerCase()?.includes(query?.toLowerCase())),
      ]
    : [
        ...QUICK_ACTIONS,
        ...recentItems,
        ...NAV_ITEMS,
      ];

  const grouped = allItems?.reduce((acc, item) => {
    const sec = item?.section;
    if (!acc?.[sec]) acc[sec] = [];
    acc?.[sec]?.push(item);
    return acc;
  }, {});

  const flatItems = Object.values(grouped)?.flat();

  const handleSelect = useCallback((item) => {
    if (item?.path) {
      addRecent({ label: item?.label, path: item?.path, icon: item?.icon });
      navigate(item?.path);
    }
    onClose();
  }, [navigate, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e?.key === 'Escape') { onClose(); return; }
      if (e?.key === 'ArrowDown') {
        e?.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, flatItems?.length - 1));
      }
      if (e?.key === 'ArrowUp') {
        e?.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
      }
      if (e?.key === 'Enter') {
        e?.preventDefault();
        if (flatItems?.[selectedIndex]) handleSelect(flatItems?.[selectedIndex]);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, flatItems, selectedIndex, handleSelect, onClose]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Scroll selected item into view
  useEffect(() => {
    const el = listRef?.current?.querySelector(`[data-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  let flatIndex = 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh]">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.15 }}
            className="relative w-full max-w-xl bg-background border border-border rounded-2xl shadow-2xl overflow-hidden mx-4"
          >
            {/* Search Input */}
            <div className="flex items-center px-4 py-3 border-b border-border">
              <Icon name="Search" size={18} className="text-muted-foreground mr-3 flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => setQuery(e?.target?.value)}
                placeholder="Search or jump to..."
                className="flex-1 bg-transparent text-foreground placeholder-muted-foreground text-sm outline-none"
              />
              <kbd className="hidden sm:flex items-center px-2 py-1 text-xs text-muted-foreground bg-muted rounded border border-border ml-2">ESC</kbd>
            </div>

            {/* Results */}
            <div ref={listRef} className="max-h-[400px] overflow-y-auto py-2">
              {flatItems?.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">No results found</div>
              ) : (
                Object.entries(grouped)?.map(([section, items]) => (
                  <div key={section}>
                    <div className="px-4 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {section}
                    </div>
                    {items?.map((item) => {
                      const idx = flatIndex++;
                      const isSelected = idx === selectedIndex;
                      return (
                        <button
                          key={`${item?.label}-${idx}`}
                          data-index={idx}
                          onClick={() => handleSelect(item)}
                          onMouseEnter={() => setSelectedIndex(idx)}
                          className={`w-full flex items-center px-4 py-2.5 text-sm transition-colors ${
                            isSelected ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
                          }`}
                        >
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center mr-3 flex-shrink-0 ${
                            isSelected ? 'bg-primary/20' : 'bg-muted'
                          }`}>
                            <Icon name={item?.icon} size={14} className={isSelected ? 'text-primary' : 'text-muted-foreground'} />
                          </div>
                          <span className="flex-1 text-left">{item?.label}</span>
                          {section === 'Quick Actions' && (
                            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">Action</span>
                          )}
                          {isSelected && (
                            <kbd className="ml-2 text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded border border-border">↵</kbd>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center space-x-3">
                <span className="flex items-center space-x-1"><kbd className="bg-muted px-1.5 py-0.5 rounded border border-border">↑↓</kbd><span>navigate</span></span>
                <span className="flex items-center space-x-1"><kbd className="bg-muted px-1.5 py-0.5 rounded border border-border">↵</kbd><span>select</span></span>
              </div>
              <span>Wilcart CRM</span>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CommandPalette;
